import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SettingModalComponent } from './layout/components/setting-modal/setting-modal.component';
import { ConstantsService } from './core/services/constants.service';
import { ContentHeightService } from './core/services/content-height.service';
import { Store } from './store';
import { User } from './core/models/User';
import { Router } from '@angular/router';
import { Chart } from 'chart.js';
import * as _ from 'underscore';
import { ApplicationEventsService } from './core/services/application-events.service';
import { isNil } from 'lodash-es';

import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import moment from 'moment';
import { UserProfileResolverService } from './core/services/user-profile-resolver.service';
import { DataService } from './core/services/data.service';
import { environment } from 'src/environments/environment';
import packageJson from '../../package.json'; 
import { SSOUser } from '@frmk2sjs/services';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    legalInfoTitle!: string;
    legalInfoUrl!: string;
    user !: User;
    angularVersion!: string;
    isAdmin: boolean;
    isSuperAdmin: boolean;

    lastPricingExecDate = '';
    private unsubscribe: Subject<void> = new Subject();

    @ViewChild('content', { static: true }) contentRef!: ElementRef;

    constructor(private modalService: NgbModal, private router: Router, private constantsService: ConstantsService,
        private dataService: DataService, private store: Store, private appEvents: ApplicationEventsService,
        private contentHeightService: ContentHeightService, private userProfileResolverService: UserProfileResolverService,
        private ssoUser: SSOUser) {
        
          this.angularVersion = packageJson.dependencies['@angular/core'];
    }

    ngOnInit() {
        console.log('SSO User full object:', JSON.parse(JSON.stringify(this.ssoUser)));
        this.subscribeUserProfileChange();
        this.setupChartExtensionAndPlugin();
        /*
         * Redirect users that click on legal information ONLY IN PROD,
         * otherwise just display a "not available" tooltip.
         */
        if (isNil(environment['legalInfoUrl'])) {
            this.legalInfoTitle = 'only available in PROD mode!';
        } else {
            this.legalInfoUrl = environment['legalInfoUrl'];
        }
    }
    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    openSetting() {
        this.dataService.getRawUser(this.user.uid).pipe(takeUntil(this.unsubscribe)).subscribe(rawUser => {
            const modalRef = this.modalService.open(SettingModalComponent, { centered: true, windowClass: 'setting-modal' });
            modalRef.result.then((result) => {
                if (result.userSelected) {
                    this.dataService.updateUserSettings(result.userList)
                        .subscribe(success => {
                            if (!success) {
                                alert('Error happened while trying update setting data.');
                            }
                            this.dataService.clearCacheData();
                            this.userProfileResolverService.fetchUser().subscribe();
                        }, error => {
                            alert('Error happened while trying update setting data.');
                            console.log(error)
                        });
                } else {

                    this.dataService.updateSettings(result.queryConfigSetting)
                        .subscribe(success => {
                            if (!success) {
                                alert('Error happened while trying update setting data.');
                            }
                            this.dataService.clearCacheData();
                            this.userProfileResolverService.fetchUser().subscribe();
                        }, error => {
                            alert('Error happened while trying update setting data.');
                            console.log(error)
                        });
                }
            }, (reason) => {

            }).catch(() => { });
            modalRef.componentInstance.queryConfig = this.user.queryConfig;
            modalRef.componentInstance.rawUser = rawUser;
        });
    }

    openDerivativesSetting() {
    }

    public subscribeUserProfileChange() {

       this.store.select<User>('user')
            .pipe(filter(Boolean))
            .subscribe((user: User) => {
                if (!user || !user.queryConfig || !user.queryConfig.entityTailConfigs) {
                    return;
                }
                if (user.isAdmin) {
                    this.isAdmin = true;
                }
                if (user.isSuperAdmin) {
                    this.isSuperAdmin = true;
                }
                this.user = user;
                this.dataService.getLastPricingExecDate().pipe(takeUntil(this.unsubscribe)).subscribe(
                    x => {
                        const formattedDate = moment(x, 'DD/MM/YYYY').format('YYYY-MM-DD');
                        this.lastPricingExecDate = "valuation date: " + moment(formattedDate, 'YYYY-MM-DD').format('DD MMM YYYY');
                    });
            });
    }

    public setupChartExtensionAndPlugin() {
        //this.registerFillBetweenLinesPlugin('derivativeVsCollateralChart');
        //this.drawCustomLegends('entityCcrExeChart');
        //this.drawVerticalLine('derivativeVsCollateralChart');
    }

    public registerFillBetweenLinesPlugin(chartName: string) {
        Chart.register({
            id:"p1",
            beforeDatasetsDraw: function (chart: any) {
                if (chart.config.options.name === undefined || chart.config.options.name !== chartName) {
                    return;
                }

                const ctx = chart.chart.ctx;
                const datasets = chart.data.datasets;
                ctx.save();

                for (let d = 0; d < datasets.length - 3; d++) {
                    const dataset = datasets[d];
                    if (dataset.fillBetweenSet === undefined) {
                        continue;
                    }

                    // get meta for both data sets
                    const meta1 = chart.getDatasetMeta(d);
                    const meta2 = chart.getDatasetMeta(dataset.fillBetweenSet);

                    // do not draw fill if one of the datasets is hidden
                    if (meta1.hidden || meta2.hidden) {
                        continue;
                    }

                    // create fill areas in pairs
                    for (let p = 0; p < meta1.data.length; p++) {
                        // if null skip
                        if (dataset.data[p] == null || dataset.data[p + 1] == null) {
                            continue;
                        }

                        if (isInsectionDataSets(meta1, meta2, p)) {
                            handleIntersection(ctx, meta1, meta2, p);
                        } else {
                            ctx.beginPath();
                            traceLine1(ctx, meta1, p);
                            connectDataset1ToDataset2(ctx, meta2, p);
                            // close the loop and fill with shading
                            ctx.closePath();
                            ctx.fillStyle = meta1.data[p + 1]._view.y >= meta2.data[p + 1]._view.y ?
                                '#cee4e0' : '#f8d2d7';
                            ctx.fill();
                        }
                    } // end for p loop
                }

                function handleIntersection(chartContext, meta1, meta2, index) {
                    const curr1 = meta1.data[index];
                    const next1 = meta1.data[index + 1];

                    const cBez1 = [{ x: curr1._view.x, y: curr1._view.y },
                    { x: curr1._view.controlPointNextX, y: curr1._view.controlPointNextY },
                    { x: next1._view.controlPointPreviousX, y: next1._view.controlPointPreviousY },
                    { x: next1._view.x, y: next1._view.y }];
                    const set1Points = findCBezPoints(cBez1);

                    const curr2 = meta2.data[index + 1];
                    const next2 = meta2.data[index];
                    const cBez2 = [{ x: next2._view.x, y: next2._view.y },
                    { x: next2._view.controlPointNextX, y: next2._view.controlPointNextY },
                    { x: curr2._view.controlPointPreviousX, y: curr2._view.controlPointPreviousY }, { x: curr2._view.x, y: curr2._view.y }];
                    const set2Points = findCBezPoints(cBez2);
                    let list = getIntersectionPoint(set1Points, set2Points);

                    if (list.length === 0) {
                        list = getIntersectionPointWithRound(set1Points, set2Points);
                    }

                    if (list.length > 0) {
                        fillIntersectionPoints(list, set1Points, set2Points, chartContext, curr1, curr2);
                    }
                }

                function fillIntersectionPoints(intersectionPoints, set1Points, set2Points, chartContext, dataSet1Secondpoint,
                    dataSet2Secondpoint) {
                    chartContext.beginPath();
                    chartContext.fillStyle = (set1Points[set1Points.length - 1].y > set2Points[set2Points.length - 1].y) ?
                        '#cee4e0' : '#f8d2d7';
                    drawPlots(set1Points, intersectionPoints[0].set1Index, set1Points.length, chartContext);
                    drawPlotsReverse(set2Points, set2Points.length - 1, intersectionPoints[0].set2Index, chartContext);
                    chartContext.lineTo(dataSet2Secondpoint._view.x, dataSet2Secondpoint._view.y);
                    chartContext.closePath();
                    chartContext.fill();

                    chartContext.beginPath();
                    chartContext.fillStyle = (set1Points[0].y < set2Points[0].y) ? '#f8d2d7' : '#cee4e0';
                    drawPlots(set1Points, 0, intersectionPoints[0].set1Index, chartContext);
                    drawPlotsReverse(set2Points, intersectionPoints[0].set2Index, 0, chartContext);
                    chartContext.closePath();
                    chartContext.fill();
                }

                function traceLine1(chartContext, meta1, index) {
                    // trace line 1
                    const curr = meta1.data[index];
                    const next = meta1.data[index + 1];
                    chartContext.moveTo(curr._view.x, curr._view.y);
                    chartContext.lineTo(curr._view.x, curr._view.y);
                    if (curr._view.steppedLine === true) {
                        chartContext.lineTo(next._view.x, curr._view.y);
                        chartContext.lineTo(next._view.x, next._view.y);
                    } else if (next._view.tension === 0) {
                        chartContext.lineTo(next._view.x, next._view.y);
                    } else {
                        chartContext.bezierCurveTo(
                            curr._view.controlPointNextX,
                            curr._view.controlPointNextY,
                            next._view.controlPointPreviousX,
                            next._view.controlPointPreviousY,
                            next._view.x,
                            next._view.y
                        );
                    }
                }

                function connectDataset1ToDataset2(chartContext, meta2, index) {
                    // connect dataset1 to dataset2
                    const curr = meta2.data[index + 1];
                    const next = meta2.data[index];
                    chartContext.lineTo(curr._view.x, curr._view.y);

                    // trace BACKWORDS set2 to complete the box
                    if (curr._view.steppedLine === true) {
                        chartContext.lineTo(curr._view.x, next._view.y);
                        chartContext.lineTo(next._view.x, next._view.y);
                    } else if (next._view.tension === 0) {
                        chartContext.lineTo(next._view.x, next._view.y);
                    } else {
                        // reverse bezier
                        chartContext.bezierCurveTo(
                            curr._view.controlPointPreviousX,
                            curr._view.controlPointPreviousY,
                            next._view.controlPointNextX,
                            next._view.controlPointNextY,
                            next._view.x,
                            next._view.y
                        );
                    }
                }

                function isInsectionDataSets(meta1, meta2, index) {
                    return ((meta1.data[index]._view.y > meta2.data[index]._view.y) &&
                        (meta1.data[index + 1]._view.y < meta2.data[index + 1]._view.y)) ||
                        ((meta1.data[index]._view.y < meta2.data[index]._view.y) &&
                            (meta1.data[index + 1]._view.y > meta2.data[index + 1]._view.y));
                }

                function findCBezPoints(b) {
                    const pts = [b[0]];
                    let lastPt = b[0];
                    const tests = 5000;
                    for (let t = 0; t <= tests; t++) {
                        // calc another point along the curve
                        const pt = getCubicBezierXYatT(b[0], b[1], b[2], b[3], t / tests);
                        // add the pt if it's not already in the pts[] array
                        const dx = pt.x - lastPt.x;
                        const dy = pt.y - lastPt.y;
                        const d = Math.sqrt(dx * dx + dy * dy);
                        const dInt = parseInt(d.toString(), 10);
                        if (dInt > 0 || t === tests) {
                            lastPt = pt;
                            pts.push(pt);
                        }
                    }
                    return (pts);
                }

                /*Given the 4 control points on a Bezier curve
                Get x,y at interval T along the curve (0<=T<=1)
                The curve starts when T==0 and ends when T==1*/
                function getCubicBezierXYatT(startPt, controlPt1, controlPt2, endPt, T) {
                    const x = CubicN(T, startPt.x, controlPt1.x, controlPt2.x, endPt.x);
                    const y = CubicN(T, startPt.y, controlPt1.y, controlPt2.y, endPt.y);
                    return ({
                        x: x,
                        y: y
                    });
                }

                // cubic helper formula
                function CubicN(T, a, b, c, d) {
                    const t2 = T * T;
                    const t3 = t2 * T;
                    return a + (-a * 3 + T * (3 * a - a * T)) * T + (3 * b + T * (-6 * b + b * 3 * T)) * T + (c * 3 - c * 3 * T) *
                        t2 + d * t3;
                }

                function getIntersectionPoint(set1, set2) {
                    const pointList = [];
                    for (let i = 0; i < set1.length - 1; i++) {
                        for (let l = 0; l < set2.length - 1; l++) {

                            const set1X = Math.ceil(set1[i].x);
                            const set2X = Math.ceil(set2[l].x);
                            const set1Y = Math.ceil(set1[i].y);
                            const set2Y = Math.ceil(set2[l].y);
                            const xDiff = set1X - set2X;
                            const yDiff = set1Y - set2Y;

                            if ((set1X === set2X) && (set1Y === set2Y)) {
                                pointList.push({ x: set1[i].x, y: set1[i].y, set1Index: i, set2Index: l });
                            }
                        }
                    }
                    return pointList;
                }

                function getIntersectionPointWithRound(set1, set2) {
                    const pointList = [];
                    for (let i = 0; i < set1.length - 1; i++) {
                        for (let l = 0; l < set2.length - 1; l++) {

                            const set1X = Math.round(set1[i].x);
                            const set2X = Math.round(set2[l].x);
                            const set1Y = Math.round(set1[i].y);
                            const set2Y = Math.round(set2[l].y);
                            const xDiff = set1X - set2X;
                            const yDiff = set1Y - set2Y;

                            if ((set1X === set2X) && (set1Y === set2Y)) {
                                pointList.push({ x: set1[i].x, y: set1[i].y, set1Index: i, set2Index: l });
                            }
                        }
                    }
                    return pointList;
                }

                function drawPlots(pts, startIndex, endIndex, chartContext) {
                    if (startIndex === undefined || startIndex === null || endIndex === undefined || endIndex === null) {
                        startIndex = 0;
                        endIndex = pts.length;
                    }

                    chartContext.arc(pts[startIndex].x - 1.5, pts[startIndex].y, 1, 0, Math.PI * 0.5);
                    /*don't draw the last dot b/ its radius will display past the curve*/
                    for (let i = startIndex; i < endIndex; i++) {
                        chartContext.arc(pts[i].x, pts[i].y, 1, 0, Math.PI * 0.5);
                    }
                }

                function drawPlotsReverse(pts, startIndex, endIndex, chartContext) {
                    if (startIndex >= pts.length) {
                        startIndex = pts.length - 1;
                    }
                    if (endIndex < 0) {
                        endIndex = 0;
                    }

                    if (startIndex === undefined || startIndex === null || endIndex === undefined || endIndex === null) {
                        startIndex = pts.length;
                        endIndex = 0;
                    }

                    for (let i = startIndex; i > endIndex; i--) {
                        if (pts[i]) {
                            chartContext.arc(pts[i].x, pts[i].y, 1, 0, Math.PI * 0.5);

                        }
                    }
                }
            }
        });
    }

    public drawCustomLegends(chartName: string) {
        Chart.register({
            id:"p2",
            afterDatasetsDraw: function (chart: any) {
                if (chart.config.options.name !== undefined && chart.config.options.name === chartName) {
                    const datasets = chart.data.datasets;
                    _.each(datasets, (x: any, index) => {
                        if (index >= datasets.length - 2) {
                            return;
                        }
                        const datasetMeta = chart.getDatasetMeta(index);
                        const point = datasetMeta.data[datasetMeta.data.length - 1]._view;
                        const ctx = chart.chart.ctx;
                        const text = chart.config.data.datasets[index].label;
                        const textWidth = ctx.measureText(text).width;
                        ctx.save();
                        ctx.textBaseline = 'top';
                        ctx.fillStyle = x.backgroundColor;
                        ctx.fillRect(point.x + 10, point.y - 7, textWidth + 5, 15);
                        ctx.fillStyle = 'white';
                        ctx.fillText(text, point.x + 12, point.y - 6);
                        ctx.restore();
                    });
                }
            }
        });
    }

/*     private drawVerticalLine(chartName: string) {
        const originalLineController = Chart.controllers.line;
        Chart.controllers.line = Chart.controllers.line.extend({
            draw: function (ease) {
                originalLineController.prototype.draw.apply(this, arguments);
                if (this.chart.config.options.name === undefined || this.chart.config.options.name !== chartName) {
                    return;
                }

                if (this.chart.tooltip._active && this.chart.tooltip._active.length) {
                    const activePoint = this.chart.tooltip._active[0],
                        ctx = this.chart.ctx,
                        x = activePoint.tooltipPosition().x,
                        topY = this.chart.scales['der'].top,
                        bottomY = this.chart.scales['der'].bottom;

                    // draw line
                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(x, topY);
                    ctx.lineTo(x, bottomY);
                    ctx.lineWidth = 1;
                    ctx.strokeStyle = '#cccccc';
                    ctx.stroke();
                    ctx.restore();
                }
            }
        });
    } */


    /**
     * make sure that the footer is at least at the bottom of the window.
     *
     * @returns {number}
     * @memberof NotificationPanelComponent
     */
    computeMinHeight(): number {
        let ret: number = 0;
        if (this.contentRef && this.contentRef.nativeElement) {
            const rect = this.contentRef.nativeElement.getBoundingClientRect();
            if (rect.height <= window.innerHeight) {
                ret = window.innerHeight - rect.height;
            }
            this.contentHeightService.setHeight(rect.height);
        }
        if (ret < 40) {
            ret = 40;
        }
        return ret;
    }

    /**
     * Redirection to the project homepage where legal information is available.
     *
     * @returns {boolean}
     * @memberof AppComponent
     */
    public gotoLegalInfo(): boolean {
        if (!isNil(this.legalInfoUrl)) {
            window.location.replace(this.legalInfoUrl);
        }
        return false;
    }

    /**
     * Logout to SSO login page.
     *
     * @returns
     * @memberof AppComponent
     */
    logOut(){
        this.dataService.logOutUser()
    }

   
}